import { CopyableValue } from '@/components/CopyableValue/CopyableValue'
import { useOrganization } from '@/contexts/useOrganization'
import { Accordion, Box, DataList, HStack, Tag } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

export type OrgFeaturesPulseProps = {
  canRead?: boolean
}

/**
 * Pulse, as two facts: where the server is and what it is called.
 *
 * The section used to wrap those two strings in a bordered box wrapping a
 * two-column "Property / Value" table — three levels of chrome, none of which
 * the operator was reading. A `DataList` of copyable values says the same
 * thing and matches every other identifier on the page.
 */
export const OrgFeaturesPulse = ({ canRead = true }: OrgFeaturesPulseProps) => {
  const { t } = useTranslation('features')
  const { orgFeatures } = useOrganization()
  const { data: features, isLoading, isError } = orgFeatures

  const pulse = features?.pulse
  const dataIntelligenceAddon = features?.addons?.data_intelligence

  const status = pulse?.enabled
    ? t('organization.features.status.enabled')
    : t('organization.features.status.disabled')

  // A canceled subscription's entry is never removed from `features.addons`,
  // so "entry exists" alone doesn't mean "is subscribed" — the badge gates on
  // status too. The raw status text lives in the Data Intelligence panel.
  const isSubscribed =
    Boolean(dataIntelligenceAddon) &&
    dataIntelligenceAddon?.status !== 'canceled'

  const subscribedLabel = t(
    'organization.features.pulse.dataIntelligence.subscribedBadge',
  )

  return (
    <Accordion.Item
      value="pulse"
      borderColor="border.muted"
      disabled={isLoading || isError || !canRead}
    >
      <Accordion.ItemTrigger>
        <HStack flex="1" gap={2} textAlign="left">
          <Box>{t('organization.features.pulse.title')}</Box>
          <Tag.Root
            size="sm"
            colorPalette={pulse?.enabled ? 'green' : 'gray'}
            aria-label={status}
          >
            <Tag.Label>{status}</Tag.Label>
          </Tag.Root>
          {isSubscribed && (
            <Tag.Root
              size="sm"
              colorPalette="green"
              aria-label={subscribedLabel}
            >
              <Tag.Label>{subscribedLabel}</Tag.Label>
            </Tag.Root>
          )}
        </HStack>
        <Accordion.ItemIndicator />
      </Accordion.ItemTrigger>
      <Accordion.ItemContent>
        <Accordion.ItemBody pb={4}>
          {pulse?.pulseServerUrl ? (
            <DataList.Root orientation="vertical" gap={3}>
              <DataList.Item>
                <DataList.ItemLabel>
                  {t('organization.features.pulse.pulseServerUrl')}
                </DataList.ItemLabel>
                <DataList.ItemValue>
                  <CopyableValue
                    value={pulse.pulseServerUrl}
                    externalLink={pulse.pulseServerUrl}
                  />
                </DataList.ItemValue>
              </DataList.Item>
              <DataList.Item>
                <DataList.ItemLabel>
                  {t('organization.features.pulse.pulseServerId')}
                </DataList.ItemLabel>
                <DataList.ItemValue>
                  <CopyableValue
                    value={pulse.pulseServerId}
                    fontFamily="mono"
                  />
                </DataList.ItemValue>
              </DataList.Item>
            </DataList.Root>
          ) : null}
        </Accordion.ItemBody>
      </Accordion.ItemContent>
    </Accordion.Item>
  )
}
